<template>
  <div class="flex justify-center items-center absolute inset-0 z-10 pointer-events-none">
    <div v-for="(snackbarBox, i) in snackbars"
         :class="`p-4 absolute ${snackbarBoxPosition(i)}`"
         :key="i"
    >
      <transition-group name="fade"
                        :class="`flex ${snackbarPosition(i)}`"
      >
        <GrmSnackbar v-for="(snackbar, j) in snackbarBox"
                     :key="snackbar.id"
                     :isOpenExtendMenu="isOpenExtendMenu"
                     :snackbar="snackbar"
                     :snackbars="snackbars"
                     :index="j"
                     :class="snackbarSlide(i)"
                     @hide="removeSnackbar"
        >
          {{ snackbar.message }}
        </GrmSnackbar>
      </transition-group>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import {clone} from "lodash/lang";

import GrmSnackbar from "@/components/Common/snackbar/GrmSnackbar";
import {mapGetters} from "vuex";

export default {
  name: 'GrmSnackbarList',
  components: { GrmSnackbar },
  props: {
    isOpenExtendMenu: { type: Boolean, required: true },
  },
  created() {
    this._Events = clone(_Events);
    this.$roomApi.addEventListeners(this._Events, this);

    this.init();
  },
  destroyed() {
    this.$roomApi.removeEventListeners(this._Events);
  },
  data() {
    return {
      snackbarPositions: ['top', 'top-right', 'bottom-right', 'bottom', 'bottom-left', 'top-left'],
      snackbars: {},
      roomModeBars: null,
    }
  },
  computed: {
    ...mapGetters('user', [
      'me',
      'isMe',
      'getRoomUser'
    ]),
    ...mapGetters('room', [
      'roomMode',
    ]),
    ...mapGetters('roomSetting', [
        'roomSetting'
    ]),
    ...mapGetters('urlShare', [
        'urlShareHistory'
    ]),
    snackbarBoxPosition() {
      return position => {
        switch (position) {
          case 'top': return 'top-0';
          case 'top-right': return 'top-0 right-0';
          case 'bottom-right': return 'bottom-0 right-0';
          case 'bottom': return 'bottom-0';
          case 'bottom-left': return 'bottom-0 left-0';
          case 'top-left': return 'top-0 left-0';
          default: return 'top-0 right-0';
        }
      }
    },
    snackbarPosition() {
      return position => {
        switch (position) {
          case 'top': return 'justify-center';
          case 'top-right': return 'flex-col items-end';
          case 'bottom-right': return 'flex-col items-end';
          case 'bottom': return 'justify-center';
          case 'bottom-left': return 'flex-col';
          case 'top-left': return 'flex-col-reverse items-start';
          default: return 'flex-col items-end';
        }
      }
    },
    snackbarSlide() {
      return position => {
        switch (position) {
          case 'top': return 'slide-down';
          case 'top-right': return 'slide-left';
          case 'bottom-right': return 'slide-left';
          case 'bottom': return 'slide-up';
          case 'bottom-left': return 'slide-right';
          case 'top-left': return 'slide-right';
          default: return 'slide-left';
        }
      }
    }
  },
  methods: {
    init() {
      this.setupSnackbar();
    },
    setupWatch() {
      this.$watch('me.roomUserRole.roleId', (roleId) => { this.changeMeRole('roleId', roleId) });
      this.$watch('me.roomUserRole.screen', (roleId) => { this.changeMeRole('screen', roleId) });
      this.$watch('me.roomUserRole.camera', (roleId) => { this.changeMeRole('camera', roleId) });
      this.$watch('me.roomUserRole.mic', (roleId) => { this.changeMeRole('mic', roleId) });
      this.$watch('me.roomUserRole.draw', (roleId) => { this.changeMeRole('draw', roleId) });
      this.$watch('me.cameraStatus', (status) => { this.changeMeDeviceStatus('camera', status) });
      this.$watch('me.micStatus', (status) => { this.changeMeDeviceStatus('mic', status) });
      this.$watch('me.nickname', (nickname) => { this.changeMeNickname(nickname) });
      this.$watch('roomMode', (roomMode) => { this.changeRoomMode(roomMode) });
      this.$watch('urlShareHistory', (urlShareHistory) => { this.startURLShare(urlShareHistory) });
    },
    setupSnackbar() {
      this.snackbarPositions.forEach(position => {
        this.$set(this.snackbars, position, []);
      });

      Vue.prototype.$snackbar = (message, options) => {
        options = Object.assign({
          type: 'info', // info, success, warning, error
          group: '', // group name
          position: 'top-right', // top, top-right, bottom-right, bottom, bottom-left, top-left
          duration: 5000, // ms
          hideIcon: false,
        }, options);
        
        const $this = this;
        const $snackbar = {
          id: Math.random().toString(36).substr(2, 9),
          init() {
            this.show(message, options);
          },
          show(message, options={}) {
            // const id = Math.random().toString(36).substr(2, 9);
            $this.addSnackbar(this.id, message, options);
          },
          hide(){
            $this.removeSnackbar({id: this.id, position: options.position});
          },
        }
        $snackbar.init();
        return $snackbar;
      };
    },
    addSnackbar(id, message, options) {
      if (options.group) {
        this.removeSnackbar({group: options.group, position: options.position});
      }

      ['top-left', 'top', 'top-right'].includes(options.position) && this.snackbars[options.position].push({
        id,
        message,
        ...options,
      });

      ['bottom-left', 'bottom', 'bottom-right'].includes(options.position) && this.snackbars[options.position].unshift({
        id,
        message,
        ...options,
      });
    },
    removeSnackbar({id, group, position}) {
      if (group) {
        this.snackbars[position] = this.snackbars[position].filter(snackbar => snackbar.group !== group);
      }

      this.snackbars[position] = this.snackbars[position].filter(snackbar => snackbar.id !== id);
    },
    changeRoomSetting(roomSetting) {
      this.$snackbar(this.$t('snackbar.setting'));
    },
    changeRoomMode(roomMode) {
      let roomModeName;
      let bgColor;
      switch (roomMode) {
        case 'call': roomModeName = this.$t('common.call'); break;
        case 'screenShare': roomModeName = this.$t('common.screenShare'); break;
        case 'doc': roomModeName = this.$t('common.doc'); break;
        case 'media': roomModeName = this.$t('common.mediaShare'); break;
        case 'exam': roomModeName = this.$t('common.exam'); break;
        case 'CBT': roomModeName = this.$t('common.CBT'); break;
        case 'group': roomModeName = this.$t('common.groupDiscussion'); break;
        case 'mirroring': roomModeName = this.$t('common.mirroring'); break;
        default: roomModeName = 'UNKNOWN'; bgColor = 'bg-gray-700'; break;
      }
      this.roomModeBars = this.$snackbar(this.$t('snackbar.mode', { '0' : this.getStatusTag(roomModeName, bgColor) }), {
        group: 'roomMode',
        position: 'top',
        duration: 3000,
      });
    },
    changeMeRole(roleType, roleId) {
      let roleTypeName;
      let bgColor;
      switch (roleType) {
        case 'roleId': roleTypeName = this.$t('common.user'); break;
        case 'screen': roleTypeName = this.$t('common.screenShare'); break;
        case 'camera': roleTypeName = this.$t('common.device.camera'); break;
        case 'mic': roleTypeName = this.$t('common.device.mic'); break;
        case 'draw': roleTypeName = this.$t('snackbar.draw'); break;
      }

      let roleIdName;
      switch (roleId) {
        case true: roleIdName = this.$t('common.active'); bgColor = 'bg-green-700'; break;
        case false: roleIdName = this.$t('common.deactive'); bgColor = 'bg-red-700'; break;
        case 'speaker': roleIdName = this.$t('common.speaker'); break;
        case 'emcee': roleIdName = this.$t('common.emcee'); break;
        case 'participant': roleIdName = this.$t('common.participant'); break;
        default: roleIdName = 'UNKNOWN'; bgColor = 'bg-gray-700'; break;
      }
      this.$snackbar(this.$t('snackbar.role', { '0' : roleTypeName, '1' : this.getStatusTag(roleIdName, bgColor) }), { group: roleType });
    },
    changeMeDeviceStatus(deviceType, status) {
      let deviceTypeName;
      let bgColor;
      switch (deviceType) {
        case 'camera': deviceTypeName = this.$t('common.device.camera'); break;
        case 'mic': deviceTypeName = this.$t('common.device.mic'); break;
      }

      let statusName;
      switch (status) {
        case 'on': statusName = this.$t('common.on'); bgColor = 'bg-green-700'; break;
        case 'off': statusName = this.$t('common.off'); bgColor = 'bg-red-700'; break;
        default: statusName = 'UNKNOWN'; bgColor = 'bg-gray-700'; break;
      }
      this.$snackbar(this.$t('snackbar.status', { '0' : deviceTypeName, '1' : this.getStatusTag(statusName, bgColor) }), { group: deviceType });
    },
    changeMeNickname(nickname) {
      this.$snackbar(this.$t('snackbar.nickname', { '0' : this.getStatusTag(nickname) }), { group: 'nickname' });
    },
    getStatusTag(statusName, bgColor = 'bg-blue-700') {
      return `<span class="rounded mx-1 py-0.5 px-1 ${bgColor} text-xs text-white">${statusName}</span>`;
    },
    startURLShare(history) {
      if (this.me.userId !== history.sharingUserId) {
        const roomUser = this.getRoomUser(history.sharingUserId);
        this.$snackbar(
            `<span class="onClickUrl">${roomUser.nickname} 사용자가 <span class="text-red-700 animate-pulse cursor-pointer">${history.urlTitle} URL<span>를 공유 했습니다.</span>`,
            {
                      position: 'top',
                      duration: 15000,
                      actions: [
                          {
                            target: '.onClickUrl',
                            handler: 'click',
                            func: () => {
                              const width = Math.min('1280', screen.width);
                              const height = Math.min('720', screen.height);
                              window.open(history.urlPath, `url_share_${history.urlIdx}`, `width=${width},height=${height}`);
                            }
                          }
                      ]
                  }
            );
      }
    }
  }
}

const _Events = {
  room_data_init(e) {
    this.setupWatch();
  },
}
</script>